import type { CSSProperties, ReactNode } from "react"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { twMerge } from "tailwind-merge"
import { TapScrollCircle, type TapScrollCircleDragState, type TapScrollCircleHandle, type TapScrollCircleRenderProps } from "./TapScrollCircle.tsx"
import { minmax } from "./utils.ts"

/**
 * Captures mouse and touch move events globally to track dragging.
 *
 * ドラッグ操作を追跡するために、マウスとタッチの移動イベントをグローバルにキャプチャ。
 *
 * @param event The initial mouse or touch event. / 初期のマウスまたはタッチイベント。
 * @param onMove A callback function that is called when the pointer moves. / ポインターが移動したときに呼び出されるコールバック関数。
 * @param onEnd A callback function that is called when the pointer is released. / ポインターが離されたときに呼び出されるコールバック関数。
 */
const capturePointerMove = (event: React.MouseEvent | React.TouchEvent, onMove: (event: { deltaX: number; deltaY: number }) => void, onEnd?: () => void) => {
    const isTouchEvent = "touches" in event.nativeEvent
    const startPosition = isTouchEvent ? (event as React.TouchEvent).nativeEvent.touches[0] : (event as React.MouseEvent).nativeEvent

    const handlePointerMove = (moveEvent: MouseEvent | TouchEvent) => {
        // ドラッグ中のデフォルトのブラウザ動作（スクロールなど）を防止
        if (isTouchEvent && moveEvent.cancelable) {
            moveEvent.preventDefault()
        }
        const movePosition = "touches" in moveEvent ? moveEvent.touches[0] : moveEvent
        onMove({
            deltaX: movePosition.clientX - startPosition.clientX,
            deltaY: movePosition.clientY - startPosition.clientY,
        })
    }

    const handlePointerUp = () => {
        if (isTouchEvent) {
            document.removeEventListener("touchmove", handlePointerMove as (e: TouchEvent) => void)
            document.removeEventListener("touchend", handlePointerUp)
        } else {
            document.removeEventListener("mousemove", handlePointerMove as (e: MouseEvent) => void)
            document.removeEventListener("mouseup", handlePointerUp)
        }
        onEnd?.()
    }

    if (isTouchEvent) {
        document.addEventListener("touchmove", handlePointerMove as (e: TouchEvent) => void, { passive: false })
        document.addEventListener("touchend", handlePointerUp)
    } else {
        document.addEventListener("mousemove", handlePointerMove as (e: MouseEvent) => void)
        document.addEventListener("mouseup", handlePointerUp)
    }
}

/**
 * Options to customize the auxiliary tap scroll circle.
 *
 * タップスクロール用サークルをカスタマイズするためのオプション。
 */
export type ScrollBarTapCircleOptions = {
    /** Enable or disable the tap scroll circle. / タップサークルを有効化または無効化。 */
    enabled?: boolean
    /** Circle diameter in pixels. / サークルの直径 (ピクセル)。 */
    size?: number
    /** Horizontal offset in pixels. / 水平方向オフセット (ピクセル)。 */
    offsetX?: number
    /** Vertical offset in pixels. / 垂直方向オフセット (ピクセル)。 */
    offsetY?: number
    /** Additional class names for the circle. / サークルの追加クラス名。 */
    className?: string
    /** Maximum drag distance used for visual feedback. / ビジュアルフィードバックに利用する最大ドラッグ距離。 */
    maxVisualDistance?: number
    /** Maximum speed multiplier relative to viewport size. / ビューポートサイズに対する最大速度倍率。 */
    maxSpeedMultiplier?: number
    /** Base opacity multiplier for the circle visuals. / サークル表示の基準透明度倍率。 */
    opacity?: number
    /** Custom visual renderer for the tap circle. / タップサークルのカスタムビジュアルのレンダラー。 */
    renderVisual?: (props: TapScrollCircleRenderProps) => ReactNode
}

type ResolvedTapScrollCircleOptions = Required<Omit<ScrollBarTapCircleOptions, "className" | "maxVisualDistance" | "renderVisual">> & {
    className?: string
    maxVisualDistance: number
    renderVisual?: ScrollBarTapCircleOptions["renderVisual"]
}

type TapScrollCancelEventDetail = {
    paneId?: string
}

/** Custom event name emitted when external interactions should cancel tap scrolling. */
export const TAP_SCROLL_CANCEL_EVENT = "virtualscroll:tap-scroll-cancel"

/**
 * Props for the ScrollBar component.
 *
 * ScrollBar コンポーネントの Props。
 */
export type ScrollBarProps = {
    /** The total size of the content. / コンテンツの総サイズ。 */
    contentSize: number
    /** The size of the visible area. / 表示領域のサイズ。 */
    viewportSize: number
    /** The current scroll position. / 現在のスクロール位置。 */
    scrollPosition: number
    /** A callback function that is called when the scroll position changes. / スクロール位置が変更されたときに呼び出されるコールバック関数。 */
    onScroll?: (scrollPosition: number, prevPosition: number) => void
    /** Whether grabbing the scrollbar thumb is allowed. / スクロールバーのつまみ操作を許可するかどうか。 */
    enableThumbDrag?: boolean
    /** Whether clicking on the track moves the scrollbar. / スクロールバーのトラッククリック操作を許可するかどうか。 */
    enableTrackClick?: boolean
    /** Whether arrow buttons control the scroll position. / 矢印ボタンによるスクロール操作を許可するかどうか。 */
    enableArrowButtons?: boolean
    /** Whether the scrollbar is horizontal. / スクロールバーが水平かどうか。 */
    horizontal?: boolean
    /** The width of the scrollbar. / スクロールバーの幅。 */
    scrollBarWidth?: number
    /** Additional class names for the component. / コンポーネントの追加のクラス名。 */
    className?: string
    /** The ID of the element that the scrollbar controls. / スクロールバーが制御する要素の ID。 */
    ariaControls?: string
    /** Configuration for the tap scroll circle. / タップサークルの設定。 */
    tapScrollCircleOptions?: ScrollBarTapCircleOptions
    /** Total number of scrollable items (optional). / スクロール対象アイテムの総数（任意）。 */
    itemCount?: number
}

/** The minimum size of the scrollbar thumb. / スクロールバーのつまみの最小サイズ。 */
const MIN_THUMB_SIZE = 20
const ARROW_HOLD_DELAY = 250
const ARROW_HOLD_INTERVAL = 60
const MIN_ARROW_STEP = 20
const ARROW_STEP_DIVISOR = 20
const TAP_SCROLL_MAX_DISTANCE = 220
const TAP_SCROLL_INITIAL_STATE: TapScrollCircleDragState = { active: false, offsetX: 0, offsetY: 0, distance: 0, direction: 0 }

const TAP_SCROLL_AUTO_SPEED_BASE_MULTIPLIER = 2.2
const TAP_SCROLL_AUTO_SPEED_PER_ORDER = 8
const TAP_SCROLL_AUTO_SPEED_MAX_MULTIPLIER = 120

const DEFAULT_TAP_SCROLL_CIRCLE_OPTIONS: ResolvedTapScrollCircleOptions = {
    enabled: true,
    size: 40,
    offsetX: -80,
    offsetY: 0,
    className: undefined,
    maxVisualDistance: TAP_SCROLL_MAX_DISTANCE,
    maxSpeedMultiplier: TAP_SCROLL_AUTO_SPEED_BASE_MULTIPLIER,
    opacity: 1,
    renderVisual: undefined,
}

/**
 * Computes the auto-scroll speed multiplier based on item count.
 *
 * アイテム数に応じた自動スクロール速度倍率を算出。
 */
const computeAutoTapScrollMaxSpeedMultiplier = (itemCount?: number) => {
    if (!itemCount || itemCount <= 0) {
        return TAP_SCROLL_AUTO_SPEED_BASE_MULTIPLIER
    }

    const clampedCount = Math.max(1, itemCount)
    const ordersOfMagnitude = Math.log10(clampedCount)
    const multiplier = TAP_SCROLL_AUTO_SPEED_BASE_MULTIPLIER + ordersOfMagnitude * TAP_SCROLL_AUTO_SPEED_PER_ORDER
    return minmax(multiplier, TAP_SCROLL_AUTO_SPEED_BASE_MULTIPLIER, TAP_SCROLL_AUTO_SPEED_MAX_MULTIPLIER)
}

/**
 * A custom scrollbar component.
 *
 * カスタムスクロールバーコンポーネント。
 */
export const ScrollBar = ({ contentSize, viewportSize, scrollPosition, onScroll, enableThumbDrag = true, enableTrackClick = true, enableArrowButtons = true, horizontal = false, scrollBarWidth = 12, className, ariaControls, tapScrollCircleOptions, itemCount }: ScrollBarProps) => {
    const [isDragging, setIsDragging] = useState(false)
    const [isThumbHovered, setIsThumbHovered] = useState(false)
    const [isTapActive, setIsTapActive] = useState(false)
    const thumbRef = useRef<HTMLDivElement>(null)
    const latestScrollPositionRef = useRef(scrollPosition)
    const arrowHoldIntervalRef = useRef<number | null>(null)
    const arrowHoldTimeoutRef = useRef<number | null>(null)
    const tapDragStateRef = useRef<TapScrollCircleDragState>(TAP_SCROLL_INITIAL_STATE)
    const tapCircleHandleRef = useRef<TapScrollCircleHandle | null>(null)
    const autoScrollFrameRef = useRef<number | null>(null)
    const lastAutoScrollTimestampRef = useRef<number | null>(null)
    const resolvedTapScrollOptions = useMemo<ResolvedTapScrollCircleOptions>(() => {
        const manualMaxSpeedMultiplier = tapScrollCircleOptions?.maxSpeedMultiplier
        const maxSpeedMultiplier = typeof manualMaxSpeedMultiplier === "number" ? manualMaxSpeedMultiplier : computeAutoTapScrollMaxSpeedMultiplier(itemCount)

        return {
            enabled: tapScrollCircleOptions?.enabled ?? DEFAULT_TAP_SCROLL_CIRCLE_OPTIONS.enabled,
            size: tapScrollCircleOptions?.size ?? DEFAULT_TAP_SCROLL_CIRCLE_OPTIONS.size,
            offsetX: tapScrollCircleOptions?.offsetX ?? DEFAULT_TAP_SCROLL_CIRCLE_OPTIONS.offsetX,
            offsetY: tapScrollCircleOptions?.offsetY ?? DEFAULT_TAP_SCROLL_CIRCLE_OPTIONS.offsetY,
            className: tapScrollCircleOptions?.className ?? DEFAULT_TAP_SCROLL_CIRCLE_OPTIONS.className,
            maxVisualDistance: tapScrollCircleOptions?.maxVisualDistance ?? DEFAULT_TAP_SCROLL_CIRCLE_OPTIONS.maxVisualDistance,
            maxSpeedMultiplier,
            opacity: minmax(tapScrollCircleOptions?.opacity ?? DEFAULT_TAP_SCROLL_CIRCLE_OPTIONS.opacity, 0, 1),
            renderVisual: tapScrollCircleOptions?.renderVisual ?? DEFAULT_TAP_SCROLL_CIRCLE_OPTIONS.renderVisual,
        }
    }, [itemCount, tapScrollCircleOptions])
    const {
        enabled: tapCircleEnabled,
        size: tapCircleSize,
        offsetX: tapCircleOffsetX,
        offsetY: tapCircleOffsetY,
        className: tapCircleClassName,
        maxVisualDistance: tapCircleMaxDistance,
        maxSpeedMultiplier: tapCircleMaxSpeedMultiplier,
        opacity: tapCircleOpacity,
        renderVisual: tapCircleRenderVisual,
    } = resolvedTapScrollOptions
    const mainSizeKey: "width" | "height" = horizontal ? "width" : "height"
    const crossSizeKey: "width" | "height" = horizontal ? "height" : "width"
    const positionKey: "left" | "top" = horizontal ? "left" : "top"
    const clientCoordinateKey: "clientX" | "clientY" = horizontal ? "clientX" : "clientY"
    /**
     * Selects delta value based on orientation.
     *
     * 向きに応じて移動量を選択。
     */
    const selectDelta = (deltaX: number, deltaY: number) => (horizontal ? deltaX : deltaY)
    /**
     * Extracts pointer coordinate based on orientation.
     *
     * 向きに基づいて座標を取得。
     */
    const getPointerCoordinate = (point: { clientX: number; clientY: number }) => point[clientCoordinateKey]
    const arrowLabels = horizontal ? ["Scroll left", "Scroll right"] : ["Scroll up", "Scroll down"]
    const arrowIcons = horizontal ? ["◀", "▶"] : ["▲", "▼"]
    const directionClass = horizontal ? "flex flex-row items-stretch" : "flex flex-col items-stretch"
    const effectiveTapMaxDistance = Math.max(tapCircleMaxDistance, 1)
    // 表示領域に対するコンテンツの比率
    const scrollRatio = viewportSize / contentSize
    const arrowButtonLength = scrollBarWidth
    const trackLength = Math.max(viewportSize - arrowButtonLength * 2, 0)
    const rawThumbSize = scrollRatio * trackLength
    const thumbSize = Math.min(Math.max(MIN_THUMB_SIZE, rawThumbSize || 0), trackLength || MIN_THUMB_SIZE)
    // 最大スクロール位置
    const maxScrollPosition = contentSize - viewportSize
    const effectiveTrackLength = Math.max(trackLength - thumbSize, 0)
    const thumbPosition = maxScrollPosition <= 0 || effectiveTrackLength <= 0 ? 0 : (scrollPosition / maxScrollPosition) * effectiveTrackLength

    // スクロールバーが表示されるかどうか
    const scrollBarVisible = contentSize > viewportSize
    const canUseArrowButtons = scrollBarVisible && enableArrowButtons

    useEffect(() => {
        latestScrollPositionRef.current = scrollPosition
    }, [scrollPosition])

    useEffect(() => {
        if (!enableThumbDrag) {
            setIsThumbHovered(false)
        }
    }, [enableThumbDrag])

    // ホバーやドラッグの状態に応じてつまみの見た目を更新
    useEffect(() => {
        const element = thumbRef.current
        if (!element) {
            return
        }

        const hoverScale = horizontal ? "scaleY(1.06)" : "scaleX(1.06)"
        const dragScale = horizontal ? "scaleY(1.12)" : "scaleX(1.12)"

        if (!enableThumbDrag) {
            element.style.removeProperty("transform")
            element.style.backgroundColor = "#7F7F7F"
            element.style.removeProperty("transition")
            return
        }

        if (isDragging) {
            element.style.transform = dragScale
            element.style.backgroundColor = "#4F4F4F"
            element.style.transition = "transform 60ms ease-out"
            return
        }

        element.style.transition = "transform 80ms ease-out"
        if (isThumbHovered) {
            element.style.transform = hoverScale
            element.style.backgroundColor = "#5F5F5F"
        } else {
            element.style.removeProperty("transform")
            element.style.backgroundColor = "#7F7F7F"
        }
    }, [enableThumbDrag, horizontal, isDragging, isThumbHovered])

    /**
     * Clears timers controlling arrow auto-repeat.
     *
     * 矢印ボタンの自動リピート用タイマーを解除。
     */
    const clearArrowTimers = useCallback(() => {
        if (arrowHoldIntervalRef.current !== null) {
            window.clearInterval(arrowHoldIntervalRef.current)
            arrowHoldIntervalRef.current = null
        }
        if (arrowHoldTimeoutRef.current !== null) {
            window.clearTimeout(arrowHoldTimeoutRef.current)
            arrowHoldTimeoutRef.current = null
        }
    }, [])

    /**
     * Stops the tap circle auto-scroll animation.
     *
     * タップサークルによる自動スクロールを停止。
     */
    const stopAutoScroll = useCallback(() => {
        if (autoScrollFrameRef.current !== null) {
            window.cancelAnimationFrame(autoScrollFrameRef.current)
            autoScrollFrameRef.current = null
        }
        lastAutoScrollTimestampRef.current = null
    }, [])

    /**
     * Resets tap circle state and related motion.
     *
     * タップサークルの状態と動作を初期化。
     */
    const resetTapScroll = useCallback(() => {
        tapDragStateRef.current = { ...TAP_SCROLL_INITIAL_STATE }
        setIsTapActive(false)
        tapCircleHandleRef.current?.reset()
        stopAutoScroll()
    }, [stopAutoScroll])

    /**
     * Advances tap circle auto-scroll by animation frame.
     *
     * タップサークルの自動スクロールをフレームごとに進行。
     */
    const stepAutoScroll = useCallback(
        (timestamp: number) => {
            const state = tapDragStateRef.current
            if (!state.active || state.direction === 0) {
                stopAutoScroll()
                return
            }
            if (!scrollBarVisible || maxScrollPosition <= 0) {
                stopAutoScroll()
                return
            }

            const lastTimestamp = lastAutoScrollTimestampRef.current ?? timestamp
            const deltaSeconds = Math.max((timestamp - lastTimestamp) / 1000, 0)
            lastAutoScrollTimestampRef.current = timestamp

            if (deltaSeconds <= 0) {
                autoScrollFrameRef.current = window.requestAnimationFrame(stepAutoScroll)
                return
            }

            const normalized = Math.min(state.distance, effectiveTapMaxDistance) / effectiveTapMaxDistance
            const eased = normalized ** 1.1
            const minSpeed = Math.max(viewportSize * 0.2, 40)
            const maxSpeed = Math.max(viewportSize * tapCircleMaxSpeedMultiplier, 1200)
            const speed = minSpeed + (maxSpeed - minSpeed) * eased

            const prevPosition = latestScrollPositionRef.current
            const nextPosition = minmax(prevPosition + state.direction * speed * deltaSeconds, 0, maxScrollPosition)

            if (nextPosition === prevPosition) {
                stopAutoScroll()
                return
            }

            latestScrollPositionRef.current = nextPosition
            onScroll?.(nextPosition, prevPosition)

            autoScrollFrameRef.current = window.requestAnimationFrame(stepAutoScroll)
        },
        [effectiveTapMaxDistance, maxScrollPosition, onScroll, scrollBarVisible, stopAutoScroll, tapCircleMaxSpeedMultiplier, viewportSize],
    )

    /**
     * Starts tap circle auto-scroll loop.
     *
     * タップサークルによる自動スクロールを開始。
     */
    const startAutoScroll = useCallback(() => {
        if (autoScrollFrameRef.current === null) {
            lastAutoScrollTimestampRef.current = null
            autoScrollFrameRef.current = window.requestAnimationFrame(stepAutoScroll)
        }
    }, [stepAutoScroll])

    useEffect(() => {
        return () => {
            clearArrowTimers()
            stopAutoScroll()
        }
    }, [clearArrowTimers, stopAutoScroll])

    /**
     * Handles tap circle drag state changes.
     *
     * タップサークルのドラッグ状態変化を処理。
     */
    const handleTapCircleDragChange = useCallback(
        (state: TapScrollCircleDragState) => {
            tapDragStateRef.current = state
            setIsTapActive(state.active)
            if (state.active && state.direction !== 0) {
                startAutoScroll()
            } else {
                stopAutoScroll()
            }
        },
        [startAutoScroll, stopAutoScroll],
    )

    useEffect(() => {
        if (!tapCircleEnabled) {
            resetTapScroll()
        }
    }, [resetTapScroll, tapCircleEnabled])

    useEffect(() => {
        const handleTapScrollCancel = (event: Event) => {
            const customEvent = event as CustomEvent<TapScrollCancelEventDetail>
            const targetPaneId = customEvent.detail?.paneId
            if (targetPaneId && ariaControls && targetPaneId !== ariaControls) {
                return
            }
            resetTapScroll()
        }

        window.addEventListener(TAP_SCROLL_CANCEL_EVENT, handleTapScrollCancel as EventListener)
        return () => {
            window.removeEventListener(TAP_SCROLL_CANCEL_EVENT, handleTapScrollCancel as EventListener)
        }
    }, [ariaControls, resetTapScroll])

    useEffect(() => {
        if (!tapCircleEnabled) {
            return
        }
        const handlePointerDown = (event: PointerEvent) => {
            if (!tapDragStateRef.current.active) {
                return
            }
            const targetNode = event.target
            if (!(targetNode instanceof Node)) {
                resetTapScroll()
                return
            }
            const element = tapCircleHandleRef.current?.getElement()
            if (element?.contains(targetNode)) {
                return
            }
            resetTapScroll()
        }
        document.addEventListener("pointerdown", handlePointerDown, true)
        return () => {
            document.removeEventListener("pointerdown", handlePointerDown, true)
        }
    }, [resetTapScroll, tapCircleEnabled])

    /**
     * Translates the thumb position to a scroll position.
     *
     * つまみの位置をスクロール位置に変換。
     *
     * @param thumbPosition The position of the thumb. / つまみの位置。
     * @returns The scroll position. / スクロール位置。
     */
    const translateToScrollPosition = (thumbPositionValue: number) => {
        if (!scrollBarVisible || effectiveTrackLength <= 0 || maxScrollPosition <= 0) {
            return 0
        }
        const clampedThumbPosition = minmax(thumbPositionValue, 0, effectiveTrackLength)
        return minmax((clampedThumbPosition / effectiveTrackLength) * maxScrollPosition, 0, maxScrollPosition)
    }

    /**
     * Moves scroll position by a fixed step using arrow controls.
     *
     * 矢印操作で一定ステップ分スクロール。
     */
    const scrollByStep = (direction: 1 | -1) => {
        if (!scrollBarVisible || maxScrollPosition <= 0) {
            return
        }
        const step = Math.max(Math.round(viewportSize / ARROW_STEP_DIVISOR), MIN_ARROW_STEP)
        const prev = latestScrollPositionRef.current
        const next = minmax(prev + direction * step, 0, maxScrollPosition)
        if (next === prev) {
            return
        }
        latestScrollPositionRef.current = next
        onScroll?.(next, prev)
    }

    /**
     * Handles pointer release on arrow buttons.
     *
     * 矢印ボタン解放時の処理。
     */
    const handleArrowPointerUp = () => {
        clearArrowTimers()
    }

    /**
     * Handles pointer press on arrow buttons.
     *
     * 矢印ボタン押下時の処理。
     */
    const handleArrowPointerDown = (direction: 1 | -1) => (event: React.MouseEvent<HTMLButtonElement> | React.TouchEvent<HTMLButtonElement>) => {
        if (!canUseArrowButtons) {
            return
        }
        event.preventDefault()
        event.stopPropagation()
        resetTapScroll()
        clearArrowTimers()
        scrollByStep(direction)
        arrowHoldTimeoutRef.current = window.setTimeout(() => {
            arrowHoldIntervalRef.current = window.setInterval(() => {
                scrollByStep(direction)
            }, ARROW_HOLD_INTERVAL)
        }, ARROW_HOLD_DELAY)
    }

    /**
     * Handles keyboard activation of arrow buttons.
     *
     * 矢印ボタンのキーボード操作を処理。
     */
    const handleArrowKeyDown = (direction: 1 | -1) => (event: React.KeyboardEvent<HTMLButtonElement>) => {
        if (!enableArrowButtons) {
            return
        }
        if (event.key === "Enter" || event.key === " " || event.key === "Spacebar") {
            event.preventDefault()
            scrollByStep(direction)
        }
    }

    // スクロールバーのつまみをマウスで押したときのハンドラ
    /**
     * Handles pointer down interaction on the thumb.
     *
     * つまみ押下時のインタラクションを処理。
     */
    const handlePointerDownOnThumb = (event: React.MouseEvent | React.TouchEvent) => {
        // スクロールバーが表示されていない場合は何もしない
        if (!scrollBarVisible) {
            return
        }
        if (!enableThumbDrag) {
            event.preventDefault()
            event.stopPropagation()
            return
        }
        // マウスの場合、左ボタン以外のクリックや Ctrl キーが押されている場合は何もしない
        if (("button" in event && event.button !== 0) || event.ctrlKey) {
            return
        }
        // event.preventDefault() // ここでは preventDefault しない
        event.stopPropagation()
        resetTapScroll()

        // ドラッグ開始時のつまみの位置
        const startThumbPosition = thumbPosition
        setIsDragging(true)
    setIsThumbHovered(true)

        // マウスの移動をキャプチャ
        capturePointerMove(
            event,
            ({ deltaX, deltaY }) => {
                const delta = selectDelta(deltaX, deltaY)
                onScroll?.(translateToScrollPosition(startThumbPosition + delta), thumbPosition)
            },
            () => {
                setIsDragging(false)
                if (thumbRef.current && !thumbRef.current.matches(":hover")) {
                    setIsThumbHovered(false)
                }
            },
        )
    }

    // スクロールバーのトラックをマウスで押したときのハンドラ
    /**
     * Handles pointer down interaction on the track.
     *
     * トラック押下時のインタラクションを処理。
     */
    const handlePointerDownOnTrack = (event: React.MouseEvent | React.TouchEvent) => {
        // スクロールバーが表示されていない場合は何もしない
        if (!scrollBarVisible) {
            return
        }
        if (!enableTrackClick) {
            event.preventDefault()
            event.stopPropagation()
            return
        }
        // マウスの場合、左ボタン以外のクリックや Ctrl キーが押されている場合は何もしない
        if (("button" in event && event.button !== 0) || event.ctrlKey) {
            return
        }

        const isTouchEvent = "touches" in event.nativeEvent
        const position = isTouchEvent ? (event as React.TouchEvent).nativeEvent.touches[0] : (event as React.MouseEvent).nativeEvent

        // マウスの初期位置を取得
        const startMousePosition = getPointerCoordinate(position)
        const rect = (event.currentTarget as HTMLElement).getBoundingClientRect()
        const clickPositionInTrack = startMousePosition - (horizontal ? rect.left : rect.top)
        resetTapScroll()

        // クリックした位置にスクロール
        // つまみはクリック位置の中央に配置
        const startThumbPosition = clickPositionInTrack - thumbSize / 2
        onScroll?.(translateToScrollPosition(startThumbPosition), thumbPosition)

        // マウスの移動をキャプチャしてドラッグ操作を処理
        capturePointerMove(event, ({ deltaX, deltaY }) => {
            const delta = selectDelta(deltaX, deltaY)
            onScroll?.(translateToScrollPosition(startThumbPosition + delta), thumbPosition)
        })
    }

    const tapCircleOpacityValue = useMemo(() => {
        const baseOpacity = isTapActive ? 1 : 0.8
        return minmax(baseOpacity * tapCircleOpacity, 0, 1)
    }, [isTapActive, tapCircleOpacity])

    const tapCircleStyle = useMemo<CSSProperties>(() => {
        const diameter = tapCircleSize
        const baseTop = `calc(50% - ${diameter / 2}px + ${tapCircleOffsetY}px)`
        return {
            left: tapCircleOffsetX,
            top: baseTop,
        }
    }, [tapCircleOffsetX, tapCircleOffsetY, tapCircleSize])

    /**
     * Renders an arrow button for scrollbar controls.
     *
     * スクロールバー制御用矢印ボタンを描画。
     */
    const renderArrowButton = (direction: 1 | -1, label: string, icon: string) => (
        <button
            type="button"
            className="flex items-center justify-center text-[#313131] text-xs transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#60a5fa] focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50"
            style={{
                [mainSizeKey]: arrowButtonLength,
                [crossSizeKey]: scrollBarWidth,
                backgroundColor: "#E0E0E0",
            }}
            aria-label={label}
            onMouseDown={handleArrowPointerDown(direction)}
            onTouchStart={handleArrowPointerDown(direction)}
            onMouseUp={handleArrowPointerUp}
            onMouseLeave={handleArrowPointerUp}
            onTouchEnd={handleArrowPointerUp}
            onTouchCancel={handleArrowPointerUp}
            onKeyDown={handleArrowKeyDown(direction)}
            aria-disabled={!enableArrowButtons}
            disabled={!canUseArrowButtons}>
            <span aria-hidden="true">{icon}</span>
        </button>
    )

    return (
        <div
            className={twMerge(
                "group relative cursor-default select-none",
                directionClass,
                className,
            )}
            style={{
                [mainSizeKey]: viewportSize,
                [crossSizeKey]: scrollBarWidth,
                backgroundColor: "white",
                userSelect: "none",
                position: "relative",
            }}
            role="scrollbar"
            tabIndex={-1}
            aria-controls={ariaControls}
            aria-valuenow={scrollPosition}
            aria-valuemin={0}
            aria-valuemax={maxScrollPosition}
            aria-orientation={horizontal ? "horizontal" : "vertical"}>
            {!horizontal && scrollBarVisible && tapCircleEnabled && (
                <TapScrollCircle
                    ref={tapCircleHandleRef}
                    className={twMerge(
                        "pointer-events-auto absolute transition-opacity duration-150",
                        tapCircleClassName,
                    )}
                    size={tapCircleSize}
                    maxVisualDistance={effectiveTapMaxDistance}
                    style={tapCircleStyle}
                    opacity={tapCircleOpacityValue}
                    renderVisual={tapCircleRenderVisual}
                    onDragChange={handleTapCircleDragChange}
                />
            )}
            {renderArrowButton(-1, arrowLabels[0], arrowIcons[0])}
            {/* biome-ignore lint/a11y/noStaticElementInteractions: スクロールトラックのクリックとドラッグを有効にするため */}
            <div
                className="relative flex-1"
                style={{
                    backgroundColor: "#F5F5F5",
                    borderRadius: scrollBarWidth / 2,
                }}
                onMouseDown={handlePointerDownOnTrack}
                onTouchStart={handlePointerDownOnTrack}
                aria-disabled={!enableTrackClick}>
                {/* コンテンツがビューポートより大きい場合にのみつまみを表示 */}
                {scrollBarVisible && (
                    // スクロールバーのつまみの当たり判定を広げるためのラッパー
                    <div
                        className="group absolute"
                        style={{
                            [mainSizeKey]: thumbSize,
                            [positionKey]: thumbPosition,
                            ...(horizontal ? { top: 0, bottom: 0 } : { left: 0, right: 0 }),
                        }}
                        onMouseDown={handlePointerDownOnThumb}
                        onTouchStart={handlePointerDownOnThumb}
                        role="slider"
                        aria-orientation={horizontal ? "horizontal" : "vertical"}
                        aria-valuenow={scrollPosition}
                        aria-valuemin={0}
                        aria-valuemax={maxScrollPosition}
                        aria-disabled={!enableThumbDrag}
                        tabIndex={enableThumbDrag ? 0 : -1}>
                        {/* スクロールバーのつまみ（可視部分） */}
                        {/* biome-ignore lint/a11y/noStaticElementInteractions: スクロールバーのつまみは必要なインタラクションです */}
                        <div
                            ref={thumbRef}
                            className={twMerge(
                                "absolute",
                                horizontal
                                    ? `inset-x-0 inset-y-[1.5px] group-hover:inset-y-[-0.5px] ${isDragging ? "-inset-y-0.5" : "group-active:-inset-y-0.5"}`
                                    : `inset-x-[1.5px] inset-y-0 group-hover:inset-x-[-0.5px] ${isDragging ? "-inset-x-0.5" : "group-active:-inset-x-0.5"}`,
                            )}
                            style={{
                                backgroundColor: "#7F7F7F",
                                borderRadius: scrollBarWidth - 1,
                                cursor: enableThumbDrag ? "pointer" : "default",
                                ...(horizontal ? {
                                    left: 0,
                                    right: 0,
                                    top: 1.5,
                                    bottom: 1.5,
                                } : {
                                    top: 0,
                                    bottom: 0,
                                    left: 1.5,
                                    right: 1.5,
                                }),
                            }}
                            onMouseEnter={() => {
                                if (enableThumbDrag) {
                                    setIsThumbHovered(true)
                                }
                            }}
                            onMouseLeave={() => {
                                if (enableThumbDrag) {
                                    setIsThumbHovered(false)
                                }
                            }}
                        />
                    </div>
                )}
            </div>
            {renderArrowButton(1, arrowLabels[1], arrowIcons[1])}
        </div>
    )
}
